import AdvancedSEOHead from "@/components/seo/AdvancedSEOHead";
import { SummaryBlock } from "@/components/summary/SummaryBlock";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Settings, TrendingUp, BarChart3, ArrowRight } from "lucide-react";
import { useCta } from "@/hooks/useCta";
import PlayBadge from "@/components/PlayBadge";
import FAQAudioBlock from "@/components/FAQAudioBlock";

export default function Methode() {
  const { handleCtaClick, resolveCta } = useCta();

  const summary = "Notre méthode data-driven en 4 étapes : Audit SP-API complet, stratégie personnalisée, implémentation optimisée et suivi continu pour maximiser votre ROI Amazon.";
  
  const bullets = [
    "Audit 360° via SP-API pour identifier toutes les opportunités",
    "Stratégie personnalisée selon vos objectifs business",
    "Implémentation avec suivi temps réel des performances", 
    "Optimisation continue basée sur la data"
  ];

  return (
    <>
      <AdvancedSEOHead
        title="Notre Méthode — Cosmos Wave | Approche Data-Driven Amazon"
        description="Méthode data-driven éprouvée : audit SP-API, stratégie personnalisée, implémentation et optimisation continue pour maximiser votre ROI Amazon."
        breadcrumbs={[
          { name: "Méthode", url: "/methode" }
        ]}
        keywords={["méthode Amazon", "audit SP-API", "stratégie data-driven", "optimisation Amazon", "ROI Amazon"]}
      />

      <div className="min-h-screen bg-background">
        {/* Audio Element */}
        <audio
          id="audio-methode"
          controls
          preload="metadata"
          crossOrigin="anonymous"
          playsInline
          src="https://symdojhcmtptpjipuwmp.supabase.co/storage/v1/object/public/media/faq-methode.mp3"
          className="scroll-mt-24"
        />

        <div className="container mx-auto px-4 py-8">
          <SummaryBlock summary={summary} bullets={bullets} className="summary-chip" />

          {/* Hero Section */}
          <section className="text-center py-16 relative">
            <Badge variant="outline" className="mb-6 bg-primary/5 text-primary border-primary/20">
              Méthode Éprouvée
            </Badge>
            <h1 className="text-5xl font-bold tracking-tight text-foreground mb-6">
              Notre Méthode
              <span className="block text-primary mt-2">Data-Driven & Résultats</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Une approche structurée et data-driven pour maximiser votre performance Amazon. 
              4 étapes clés pour des résultats mesurables et durables.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button size="lg" onClick={(e) => handleCtaClick('audit', e)}>
                Commencer par l'audit
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href={resolveCta('results')}>Voir nos résultats</a>
              </Button>
            </div>

            {/* Desktop PlayBadge */}
            <PlayBadge
              audioId="audio-methode"
              size={96}
              className="hidden md:block absolute right-24 bottom-16 z-10"
            />

            {/* Mobile PlayBadge */}
            <div className="md:hidden mt-8">
              <PlayBadge
                audioId="audio-methode"
                size={72}
                className="mx-auto"
              />
            </div>
          </section>

          {/* Methodology Steps */}
          <section className="py-16">
            <h2 className="text-3xl font-bold text-center mb-12">4 étapes pour le succès</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  step: "1",
                  icon: <Search className="h-8 w-8" />,
                  title: "Audit SP-API",
                  description: "Analyse complète de votre compte Amazon via SP-API pour identifier toutes les opportunités d'optimisation et de croissance."
                },
                {
                  step: "2",
                  icon: <Settings className="h-8 w-8" />,
                  title: "Stratégie personnalisée",
                  description: "Plan d'action sur-mesure selon vos objectifs : croissance, rentabilité, expansion géographique ou lancement produit."
                },
                {
                  step: "3",
                  icon: <TrendingUp className="h-8 w-8" />,
                  title: "Implémentation",
                  description: "Mise en œuvre des optimisations Ads, SEO, catalogue et opérationnelles avec suivi temps réel des performances."
                },
                {
                  step: "4",
                  icon: <BarChart3 className="h-8 w-8" />,
                  title: "Optimisation continue",
                  description: "Monitoring quotidien, ajustements stratégiques et reporting détaillé pour une amélioration constante des résultats."
                }
              ].map((phase, index) => (
                <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {phase.step}
                  </div>
                  <div className="text-primary mb-4">{phase.icon}</div>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg">{phase.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{phase.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Garanties */}
          <section className="py-16 bg-muted/30 rounded-3xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Nos garanties</h2>
              <p className="text-xl text-muted-foreground">
                Une approche transparente et orientée résultats
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">100%</div>
                <div className="font-semibold mb-2">Transparence</div>
                <div className="text-sm text-muted-foreground">Accès total à vos données et reporting détaillé</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">7j</div>
                <div className="font-semibold mb-2">Premier audit</div>
                <div className="text-sm text-muted-foreground">Diagnostic complet sous une semaine</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">30j</div>
                <div className="font-semibold mb-2">Premiers résultats</div>
                <div className="text-sm text-muted-foreground">Améliorations visibles dès le premier mois</div>
              </div>
            </div>
          </section>

          {/* Audio Summary */}
          <FAQAudioBlock
            title="Notre méthode expliquée en 5 minutes"
            audioSrc="/media/faq-methode.mp3"
            subtitle="Découvrez notre approche data-driven et nos 4 étapes clés"
            audioId="audio-methode"
            durationHintSec={300}
          />

          {/* CTA Final */}
          <section className="text-center py-16">
            <h2 className="text-3xl font-bold mb-4">Prêt à appliquer notre méthode ?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Commençons par un audit gratuit pour identifier le potentiel de votre compte Amazon
            </p>
            <Button size="lg" onClick={(e) => handleCtaClick('audit', e)}>
              Démarrer l'audit gratuit
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </section>
        </div>
      </div>
    </>
  );
}